import { BaseTexture, Constants } from "@babylonjs/core";
class Texture3Layer {
    constructor(host, id) {
        this._host = host;
        this._depth = id;
    }
    update(data, row, column, width, height) {
        this._host.update(this._depth, data, row, column, width, height);
    }
    release() {
        this._host.release(this._depth);
    }
    get depth() {
        return this._depth;
    }
    get host() {
        return this._host;
    }
}
export class Texture3 extends BaseTexture {
    constructor(sceneOrEngine, width, height, depth, format, textureType, internalFormat, generateMipmaps, samplingMode, wrapU, wrapV) {
        super(sceneOrEngine);
        if (typeof width === "object" && width !== null) {
            const options = width;
            width = options.width;
            height = options.height;
            depth = options.depth;
            format = options.format;
            textureType = options.textureType;
            internalFormat = options.internalFormat;
            generateMipmaps = options.generateMipmap;
            samplingMode = options.samplingMode;
            wrapU = options.wrapU;
            wrapV = options.wrapV;
        }
        height = height ?? width;
        depth = depth ?? 1;
        format = format ?? Constants.TEXTUREFORMAT_RGBA;
        textureType = textureType ?? Constants.TEXTURETYPE_UNSIGNED_INT;
        generateMipmaps = generateMipmaps ?? false;
        samplingMode = samplingMode ?? Constants.TEXTURE_NEAREST_NEAREST;
        this._layers = new Array(depth).fill(null);
        this._count = 0;
        this._w = width;
        this._h = height;
        this._internalFormat = internalFormat;
        this._texture = this._getEngine().__SpaceXR__createRawTexture2DArray(width, height, depth, format, samplingMode, textureType, internalFormat);
        this.wrapU = wrapU ?? Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this.wrapV = wrapV ?? Constants.TEXTURE_CLAMP_ADDRESSMODE;
    }
    get width() {
        return this._w;
    }
    get height() {
        return this._h;
    }
    get depth() {
        return this._layers.length;
    }
    get count() {
        return this._count;
    }
    reserve() {
        let a = undefined;
        if (this.count < this.depth) {
            for (let i = 0; i != this._layers.length; i++) {
                if (this._layers[i] === null) {
                    a = this._buildLayer(i);
                    this._layers[i] = a;
                    this._count++;
                    break;
                }
            }
        }
        return a;
    }
    update(depth, data, xoffset, yoffset, width, height) {
        const engine = this._getEngine();
        if (engine && this._texture) {
            engine.__SpaceXR__updateSubRawTexture2DArray(this._texture, 0, xoffset ?? 0, yoffset ?? 0, depth, width ?? this._w, height ?? this._h, 1, data, this._texture.format, this._texture.type);
        }
    }
    release(depth) {
        if (this._count > 0 && depth >= 0 && depth < this.depth) {
            const layer = this._layers[depth];
            if (layer) {
                this._layers[depth] = null;
                this._count--;
            }
        }
    }
    ensureRoomFor(count) {
        const oldTexture = this._texture;
        if (!oldTexture) {
            return false;
        }
        if (this.depth - this.count >= count) {
            return true;
        }
        const gl = this._getEngine()?._gl;
        if (!gl) {
            return false;
        }
        const maxLayers = gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS);
        const targetDepth = count + this.depth;
        let newDepth = this.depth;
        do {
            newDepth = Math.max(count, Math.ceil(this.depth * 1.3));
        } while (newDepth < targetDepth);
        newDepth = Math.min(newDepth, maxLayers);
        if (newDepth < targetDepth) {
            console.log(`Max supported texture layers:${maxLayers}, asked for ${targetDepth}`);
            return false;
        }
        console.log(`Grow texture layers from ${this.depth} to ${newDepth}`);
        const engine = this._getEngine();
        if (!engine) {
            return false;
        }
        const newTexture = engine.__SpaceXR__createRawTexture2DArray(this._w, this._h, newDepth, oldTexture.format, oldTexture.samplingMode, oldTexture.type, this._internalFormat);
        engine.__SpaceXR__copyRawTexture2DArray(oldTexture, newTexture);
        oldTexture.dispose();
        this._texture = newTexture;
        this._layers = this._layers.concat(new Array(newDepth - this._layers.length).fill(null));
        return true;
    }
    _buildLayer(z) {
        const layer = new Texture3Layer(this, z);
        if (!layer.host) {
            console.error(`Layer host was not set correctly for depth: ${z}`);
        }
        return layer;
    }
}
//# sourceMappingURL=texture.texture3.js.map